import React, { useEffect, useState } from 'react';
import { customersApi } from '../services/api';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Loader2, RefreshCw } from 'lucide-react';

/**
 * WhatsAppRecipientPicker — live-synced picker of a customer's KYC contacts
 * used to pick which numbers receive DoubleTick WhatsApp order updates.
 *
 * When `customerId` changes it fetches GET /api/customers/{id} to get the
 * freshest `mobile` + `additional_phones`, so any KYC edit shows up here
 * without a full page reload.
 *
 * Props:
 *   customerId — id of the customer whose contacts to list
 *   value       — [{phone, name}] currently selected
 *   onChange    — (newList) => void
 *   testIdPrefix — data-testid prefix (default 'wa-recipient')
 *   emptyMessage — shown when the customer has no contacts on file
 */
const WhatsAppRecipientPicker = ({
  customerId,
  value = [],
  onChange,
  testIdPrefix = 'wa-recipient',
  emptyMessage = 'No contacts on file for this customer. Add them on the Customer KYC page.',
}) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = React.useCallback(async () => {
    if (!customerId) {
      setContacts([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await customersApi.get(customerId);
      const cust = res.data || {};
      const list = [];
      if (cust.mobile) {
        list.push({
          phone: cust.mobile,
          name: cust.contact_person_name || cust.company_name || '',
          designation: cust.contact_designation || 'Primary',
          primary: true,
        });
      }
      (cust.additional_phones || []).forEach(p => {
        if (p.phone && !list.some(x => x.phone === p.phone)) {
          list.push({
            phone: p.phone,
            name: p.name || '',
            designation: p.designation || p.label || '',
            primary: false,
          });
        }
      });
      setContacts(list);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const toggle = (contact) => {
    const exists = value.some(v => v.phone === contact.phone);
    const next = exists
      ? value.filter(v => v.phone !== contact.phone)
      : [...value, { phone: contact.phone, name: contact.name }];
    onChange(next);
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-section`}>
      <div className="flex items-center justify-between gap-2">
        <Label className="text-sm font-medium">
          WhatsApp Recipients
          <span className="ml-2 text-[10px] font-normal text-muted-foreground">
            {value.length} selected
          </span>
        </Label>
        <button
          type="button"
          onClick={load}
          disabled={loading || !customerId}
          className="text-[10px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 disabled:opacity-50"
          title="Refresh from customer's KYC"
          data-testid={`${testIdPrefix}-refresh`}
        >
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="rounded-md border bg-background overflow-hidden">
        {loading && contacts.length === 0 ? (
          <div className="flex items-center justify-center py-3 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> Loading contacts…
          </div>
        ) : error ? (
          <p className="text-xs text-red-600 dark:text-red-400 p-3">{error}</p>
        ) : contacts.length === 0 ? (
          <p className="text-xs text-muted-foreground p-3">{emptyMessage}</p>
        ) : (
          <div className="max-h-40 overflow-y-auto divide-y">
            {contacts.map(contact => {
              const checked = value.some(v => v.phone === contact.phone);
              return (
                <label
                  key={contact.phone}
                  className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-muted/50 cursor-pointer"
                  data-testid={`${testIdPrefix}-contact-${contact.phone}`}
                >
                  <Checkbox checked={checked} onCheckedChange={() => toggle(contact)} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">
                      {contact.name || 'Unnamed'}
                      {contact.designation && (
                        <span className="ml-1 text-[10px] font-normal text-muted-foreground">· {contact.designation}</span>
                      )}
                      {contact.primary && (
                        <span className="ml-1 text-[9px] px-1 rounded bg-blue-100 text-blue-700 dark:bg-blue-500/20">primary</span>
                      )}
                    </p>
                    <p className="text-[10px] text-muted-foreground truncate">{contact.phone}</p>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>
      <p className="text-[10px] text-muted-foreground">
        Ticked numbers receive automated order updates. Synced live with the customer&apos;s KYC.
      </p>
    </div>
  );
};

export default WhatsAppRecipientPicker;
